import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import apiService, { StockWarning } from '../../services/api';
import WeightToggle from '../../components/admin/WeightToggle';
import { receiptColorStyle } from '../../utils/warehouseStock';
import ReceiptItemsTable, { ReceiptItem, useClientPriceLists } from '../../components/admin/ReceiptItemsTable';
import { User } from '../../types';
import { ColorKey, getPricingConfig } from '../../utils/pricingConfig';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useEscapeToClose from '../../hooks/useEscapeToClose';
import { ChevronDown, ChevronRight, FileDown, MoreHorizontal, Plus, Search, Trash2, X } from 'lucide-react';

interface ReceiptRow {
  id: number;
  receiptNumber: string;
  customerName: string;
  concept: string;
  amountARS: number;
  paymentMethod: string;
  receiptType: string;
  clientId: number | null;
  deductWarehouseStock: boolean;
  items?: ReceiptItem[];
  rawItems: RawDetailLine[];
  createdAt: string;
  deliveryDate?: string;
  monthlyOrderId?: number | null;
}

interface DetailLine {
  mode: 'bolsas' | 'libre';
  anchoCm: number;
  largoCm: number;
  color: ColorKey;
  micronaje: number;
  unidad: 'bolsas' | 'packs';
  cantidad: number;
  bolsasPorPack: number;
  texto: string;
  deduct: boolean;
  itemNote: string;
  costUnit: number;
  saleNetUnit: number;
  vatRate: number;
  marginPercent: number;
  purchaseHasVat: boolean;
  priceListId?: number;
  orderMonth: string;
}

export type RawDetailLine =
  | { mode: 'bolsas'; ancho: number; largo: number; color: string; microns: number; unidad: 'bolsas' | 'packs'; cantidad: number; bolsasPorPack: number | null; deduct: boolean; itemNote?: string; costUnit?: number; saleNetUnit?: number; vatRate?: number; marginPercent?: number; purchaseHasVat?: boolean; priceListId?: number; orderMonth?: string }
  | { mode: 'libre'; texto: string };

const COLOR_LABELS: Record<ColorKey, string> = {
  negro: 'Negras',
  blanco: 'Blancas',
  azul: 'Azules',
  verde: 'Verdes',
  rojo: 'Rojas'
};

const COLOR_RECEIPT_LABELS: Record<ColorKey, string> = {
  negro: 'Negro',
  blanco: 'Blanco',
  azul: 'Azul',
  verde: 'Verde',
  rojo: 'Rojo'
};

// Un "pack" es siempre PACK_SIZE bolsas: el stock, los minimos y el consumo del cliente
// asumen este valor de forma fija.
const PACK_SIZE = 50;

const emptyDetailLine = (mode: 'bolsas' | 'libre'): DetailLine => ({
  mode,
  anchoCm: 45,
  largoCm: 60,
  color: 'negro',
  micronaje: 23,
  unidad: 'packs',
  cantidad: 0,
  bolsasPorPack: PACK_SIZE,
  texto: '',
  deduct: true,
  itemNote: '',
  costUnit: 0,
  saleNetUnit: 0,
  vatRate: 21,
  marginPercent: 15,
  purchaseHasVat: false,
  orderMonth: ''
});

const describeDetailLine = (line: DetailLine): string => {
  if (line.mode === 'libre') return line.texto.trim();
  if (!line.cantidad) return '';
  const colorLabel = COLOR_RECEIPT_LABELS[line.color];
  if (line.unidad === 'packs') {
    return `${line.cantidad} pack ${line.anchoCm}x${line.largoCm} x${PACK_SIZE}u Color ${colorLabel} (${line.cantidad * PACK_SIZE} bolsas)`;
  }
  return `${line.cantidad} bolsas ${line.anchoCm}x${line.largoCm} Color ${colorLabel}`;
};

const detailLineToRaw = (line: DetailLine): RawDetailLine | null => {
  if (line.mode === 'libre') {
    const texto = line.texto.trim();
    return texto ? { mode: 'libre', texto } : null;
  }
  if (!line.cantidad) return null;
  return {
    mode: 'bolsas', ancho: line.anchoCm, largo: line.largoCm, color: line.color, microns: line.micronaje,
    unidad: line.unidad, cantidad: line.cantidad, bolsasPorPack: line.unidad === 'packs' ? PACK_SIZE : null,
    deduct: line.deduct, itemNote: line.itemNote || undefined, costUnit: line.costUnit, saleNetUnit: line.saleNetUnit, vatRate: line.vatRate, marginPercent: line.marginPercent, purchaseHasVat: line.purchaseHasVat, priceListId: line.priceListId, orderMonth: line.orderMonth || undefined,
  };
};

const rawLineToDetailLine = (raw: RawDetailLine): DetailLine => {
  if (raw.mode === 'libre') {
    return { ...emptyDetailLine('libre'), texto: raw.texto };
  }
  return {
    mode: 'bolsas', anchoCm: raw.ancho, largoCm: raw.largo, color: raw.color as ColorKey, micronaje: raw.microns,
    unidad: raw.unidad, cantidad: raw.cantidad, bolsasPorPack: PACK_SIZE, texto: '', deduct: raw.deduct,
    itemNote: raw.itemNote || '', costUnit: Number(raw.costUnit || 0), saleNetUnit: Number(raw.saleNetUnit || 0), vatRate: Number(raw.vatRate ?? 21), marginPercent: Number(raw.marginPercent ?? (raw.costUnit ? ((Number(raw.saleNetUnit || 0) / Number(raw.costUnit)) - 1) * 100 : 15)), purchaseHasVat: Boolean(raw.purchaseHasVat), priceListId: raw.priceListId, orderMonth: raw.orderMonth || '',
  };
};

const money = (value: number, digits = 2) => `$${value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: digits })}`;
const lineBags = (line: DetailLine) => (line.unidad === 'packs' ? line.cantidad * PACK_SIZE : line.cantidad);

const DetailLinesEditor: React.FC<{
  lines: DetailLine[];
  onUpdate: (index: number, patch: Partial<DetailLine>) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
  priceOptions?: any[];
  monthOptions?: string[];
}> = ({ lines, onUpdate, onAdd, onRemove, priceOptions, monthOptions }) => {
  const [openDetails, setOpenDetails] = useState<Set<number>>(new Set());
  const toggleDetails = (index: number) => setOpenDetails((current) => {
    const next = new Set(current);
    if (next.has(index)) next.delete(index); else next.add(index);
    return next;
  });
  const remove = (index: number) => {
    onRemove(index);
    // Indexes after the removed line shift down by one.
    setOpenDetails((current) => new Set(Array.from(current).filter((i) => i !== index).map((i) => (i > index ? i - 1 : i))));
  };
  const field = 'w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm';
  // A price-list article already on another line is not offered again.
  const takenByOtherLine = (price: any, index: number) => lines.some((other, i) => i !== index && other.mode === 'bolsas' && (other.priceListId === Number(price.id)
    || (Number(price.widthCm) === Number(other.anchoCm) && Number(price.lengthCm) === Number(other.largoCm) && String(price.color) === other.color && Number(price.microns || 0) === Number(other.micronaje || 0))));

  return (
    <div className="space-y-2">
      {priceOptions && !priceOptions.length && <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">Este cliente todavía no tiene productos en su lista de precios.</p>}
      {lines.map((line, index) => {
        const final = line.saleNetUnit * (1 + line.vatRate / 100);
        const detailsOpen = openDetails.has(index);
        return (
          <div key={index} className="rounded-lg border border-gray-200 bg-white p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-gray-400">#{index + 1}</span>
                <div className="inline-flex overflow-hidden rounded-md border border-gray-300 text-xs">
                  <button type="button" onClick={() => onUpdate(index, { mode: 'bolsas' })} className={`px-2.5 py-1 ${line.mode === 'bolsas' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Bolsas</button>
                  <button type="button" onClick={() => onUpdate(index, { mode: 'libre' })} className={`border-l border-gray-300 px-2.5 py-1 ${line.mode === 'libre' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Texto libre</button>
                </div>
                {line.mode === 'bolsas' && (
                  <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold" style={receiptColorStyle(COLOR_LABELS[line.color])}>
                    {line.anchoCm || '?'} × {line.largoCm || '?'} · {COLOR_LABELS[line.color]}{line.micronaje ? ` · ${line.micronaje}μ` : ''}
                  </span>
                )}
              </div>
              <button type="button" onClick={() => remove(index)} disabled={lines.length <= 1} title="Quitar línea" className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            {line.mode === 'bolsas' ? (
              <div className="mt-2 space-y-2">
                {priceOptions && priceOptions.length > 0 && (
                  <select value={line.priceListId || ''} onChange={e => { const price = priceOptions.find(p => String(p.id) === e.target.value); if (!price) { onUpdate(index, { priceListId: undefined }); return; } onUpdate(index, { anchoCm: Number(price.widthCm), largoCm: Number(price.lengthCm), color: String(price.color) as ColorKey, micronaje: Number(price.microns || 0), unidad: 'packs', costUnit: Number(price.costUnit || 0), saleNetUnit: Number(price.saleNetUnit || 0), vatRate: Number(price.vatRate ?? 21), marginPercent: Number(price.marginPercent ?? (price.costUnit ? ((price.saleNetUnit / price.costUnit) - 1) * 100 : 15)), purchaseHasVat: Boolean(price.purchaseHasVat), priceListId: Number(price.id) }); }} className="w-full rounded-md border border-blue-200 bg-blue-50/50 px-2 py-1.5 text-sm text-blue-900">
                    <option value="">Elegir de la lista de precios del cliente...</option>
                    {priceOptions.map(price => takenByOtherLine(price, index) ? null : <option key={price.id} value={price.id}>{price.widthCm} x {price.lengthCm} cm · {price.color} · {price.microns}μ · Final ${Number(price.saleGrossUnit || 0).toLocaleString('es-AR', { maximumFractionDigits: 5 })}</option>)}
                  </select>
                )}
                <div className="grid grid-cols-3 gap-2 md:grid-cols-6">
                  <label className="text-[11px] text-gray-500">Ancho (cm)<input type="number" min={0} className={field} value={line.anchoCm} onChange={(e) => onUpdate(index, { anchoCm: Number(e.target.value) })} /></label>
                  <label className="text-[11px] text-gray-500">Largo (cm)<input type="number" min={0} className={field} value={line.largoCm} onChange={(e) => onUpdate(index, { largoCm: Number(e.target.value) })} /></label>
                  <label className="text-[11px] text-gray-500">Color
                    <select className={`${field} font-semibold`} style={receiptColorStyle(COLOR_LABELS[line.color])} value={line.color} onChange={(e) => onUpdate(index, { color: e.target.value as ColorKey })}>
                      {(Object.keys(COLOR_LABELS) as ColorKey[]).map((c) => <option key={c} value={c} style={receiptColorStyle(COLOR_LABELS[c])}>{COLOR_LABELS[c]}</option>)}
                    </select>
                  </label>
                  <label className="text-[11px] text-gray-500">Micrones<input type="number" min={0} className={field} value={line.micronaje} onChange={(e) => onUpdate(index, { micronaje: Number(e.target.value) })} /></label>
                  <label className="text-[11px] text-gray-500">Unidad
                    <select className={field} value={line.unidad} onChange={(e) => onUpdate(index, { unidad: e.target.value as 'bolsas' | 'packs' })}>
                      <option value="packs">Packs x{PACK_SIZE}</option>
                      <option value="bolsas">Bolsas</option>
                    </select>
                  </label>
                  <label className="text-[11px] text-gray-500">Cantidad<input type="number" min={0} className={`${field} font-semibold`} value={line.cantidad} onChange={(e) => onUpdate(index, { cantidad: Number(e.target.value) })} /></label>
                </div>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
                  <label className="inline-flex items-center gap-1.5 text-gray-600">
                    <input type="checkbox" checked={line.deduct} onChange={(e) => onUpdate(index, { deduct: e.target.checked })} />
                    Descontar de stock propio
                  </label>
                  {monthOptions && (
                    <label className="inline-flex items-center gap-1.5 text-gray-600">Mes del pedido
                      <select className="rounded-md border border-gray-300 px-1.5 py-1 text-xs" value={line.orderMonth} onChange={(e) => onUpdate(index, { orderMonth: e.target.value })}>
                        <option value="">Igual que el remito</option>
                        {Array.from(new Set([...monthOptions, ...(line.orderMonth ? [line.orderMonth] : [])])).sort().map((month) => <option key={month} value={month}>Pedido de {month}</option>)}
                      </select>
                    </label>
                  )}
                  <button type="button" onClick={() => toggleDetails(index)} className="ml-auto inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-gray-600 hover:bg-gray-50">
                    {detailsOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    Precio y nota
                    {!detailsOpen && final > 0 && <span className="font-semibold text-blue-800">· {money(final, 5)} c/u</span>}
                    {!detailsOpen && line.itemNote && <span className="rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-800">nota</span>}
                  </button>
                </div>
                {!line.deduct && <p className="text-xs text-amber-700">Esta línea no se descuenta de "Stock propio" (por ejemplo, porque vino de otro proveedor no cargado en el sistema).</p>}

                {detailsOpen && (
                  <div className="space-y-3 rounded-lg border border-blue-100 bg-blue-50/40 p-3">
                    <label className="block text-[11px] text-gray-500">Nota de esta línea (uso interno)
                      <input value={line.itemNote} onChange={(e) => onUpdate(index, { itemNote: e.target.value })} placeholder="Ej: stock propio, no de proveedor" className={field} />
                    </label>
                    <div className="flex flex-wrap items-center justify-between gap-2"><p className="text-[11px] font-semibold text-blue-900">Datos comerciales · no se imprimen</p>{line.priceListId && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Precio de la lista del cliente</span>}</div>
                    <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                      <label className="text-[11px] text-gray-500">Compra unit. sin IVA<input type="number" min={0} step="0.001" value={line.costUnit} onChange={e => { const cost = Number(e.target.value); onUpdate(index, { costUnit: cost, saleNetUnit: cost * (1 + line.marginPercent / 100) }); }} className={field} /></label>
                      <label className="text-[11px] text-gray-500">Ganancia %<input type="number" min={0} step="0.01" value={line.marginPercent} onChange={e => { const margin = Number(e.target.value); onUpdate(index, { marginPercent: margin, saleNetUnit: line.costUnit * (1 + margin / 100) }); }} className={field} /></label>
                      <label className="text-[11px] text-gray-500">IVA venta %<input type="number" min={0} step="0.01" value={line.vatRate} onChange={e => onUpdate(index, { vatRate: Number(e.target.value) })} className={field} /></label>
                      <label className="flex items-center gap-2 self-end pb-1.5 text-[11px] text-gray-600"><input type="checkbox" checked={line.purchaseHasVat} onChange={e => onUpdate(index, { purchaseHasVat: e.target.checked })} />El proveedor factura IVA</label>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600">
                      <span>Costo con IVA: <strong className="text-gray-900">{money(line.costUnit * (1 + line.vatRate / 100), 5)}</strong></span>
                      <span>Venta sin IVA: <strong className="text-gray-900">{money(line.saleNetUnit, 5)}</strong></span>
                      <span>Final con IVA: <strong className="text-blue-900">{money(final, 5)}</strong></span>
                      <span>Total de la línea: <strong className="text-blue-900">{money(final * lineBags(line))}</strong></span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <input className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" value={line.texto} onChange={(e) => onUpdate(index, { texto: e.target.value })} placeholder="Ej: 10 packs bolsas 45x60 negras" />
            )}

            {describeDetailLine(line) && <p className="mt-2 text-xs italic text-gray-400">En el remito: {describeDetailLine(line)}</p>}
          </div>
        );
      })}
      <button type="button" onClick={onAdd} className="inline-flex items-center gap-1 rounded-lg border border-dashed border-primary-300 px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50">
        <Plus className="h-4 w-4" /> Agregar línea
      </button>
    </div>
  );
};

export interface EditableReceiptItemsRow {
  id: number;
  receiptNumber: string;
  rawItems: RawDetailLine[];
  clientId?: number | null;
  monthlyOrderId?: number | null;
}

export const EditReceiptItemsModal: React.FC<{
  row: EditableReceiptItemsRow;
  onClose: () => void;
  onSaved: (patch: { concept: string; deductWarehouseStock: boolean; rawItems: RawDetailLine[]; monthlyOrderId: number | null }) => void;
}> = ({ row, onClose, onSaved }) => {
  const [lines, setLines] = useState<DetailLine[]>(row.rawItems.length ? row.rawItems.map(rawLineToDetailLine) : [emptyDetailLine('bolsas')]);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [priceOptions, setPriceOptions] = useState<any[] | undefined>(undefined);
  const [monthOptions, setMonthOptions] = useState<string[] | undefined>(undefined);
  const [clientOrders, setClientOrders] = useState<any[]>([]);
  const initialOrderId = row.monthlyOrderId ? String(row.monthlyOrderId) : '';
  const [orderChoice, setOrderChoice] = useState(initialOrderId);
  useBodyScrollLock(true);
  useEscapeToClose(true, onClose);

  useEffect(() => {
    let active = true;
    if (!row.clientId) {
      setPriceOptions(undefined);
      return () => { active = false; };
    }
    setPriceOptions(undefined);
    apiService.getClientPrices(Number(row.clientId))
      .then((data) => { if (active) setPriceOptions(data.prices || []); })
      .catch(() => { if (active) setPriceOptions([]); });
    return () => { active = false; };
  }, [row.clientId]);

  useEffect(() => {
    let active = true;
    setMonthOptions(undefined);
    setClientOrders([]);
    if (!row.clientId) return () => { active = false; };
    apiService.getMonthlyOrders({ clientId: Number(row.clientId) })
      .then((data) => {
        if (!active || !data.orders?.length) return;
        setClientOrders(data.orders);
        setMonthOptions(Array.from(new Set<string>(data.orders.map((order: any) => String(order.month)))));
      })
      .catch(() => {});
    return () => { active = false; };
  }, [row.clientId]);

  const updateLine = (index: number, patch: Partial<DetailLine>) => setLines((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  const addLine = () => setLines((current) => [...current, emptyDetailLine('bolsas')]);
  const removeLine = (index: number) => setLines((current) => (current.length > 1 ? current.filter((_, i) => i !== index) : current));

  const save = async () => {
    const editedItems = lines.map(detailLineToRaw).filter((x): x is RawDetailLine => x !== null);
    if (editedItems.length === 0) {
      setError('Agrega al menos un producto.');
      return;
    }
    try {
      setIsSaving(true);
      setError('');
      const response = await apiService.updateReceiptItems(row.id, editedItems);
      // Moving the remito to another order goes last: it clears the per-line months saved above.
      if (orderChoice !== initialOrderId) await apiService.updateReceiptMonthlyOrder(row.id, orderChoice ? Number(orderChoice) : null);
      const savedItems = orderChoice && orderChoice !== initialOrderId ? editedItems.map((item) => (item.mode === 'bolsas' ? { ...item, orderMonth: undefined } : item)) : editedItems;
      onSaved({ concept: response.concept, deductWarehouseStock: response.deductWarehouseStock, rawItems: savedItems, monthlyOrderId: orderChoice ? Number(orderChoice) : null });
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudieron actualizar los productos del remito.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[92vh] sm:max-h-[90vh] overflow-y-auto rounded-t-xl sm:rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Editar productos del remito</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl leading-none">×</button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-600">
            Remito <span className="font-medium text-gray-900">{row.receiptNumber}</span>. Corregí las líneas existentes, agregá las que falten, y marcá las que no correspondan descontarse de "Stock propio".
          </p>
          {error && <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>}
          {clientOrders.length > 0 && (
            <label className="block text-sm font-medium text-gray-700">¿A qué pedido corresponde este remito?
              <select className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={orderChoice} onChange={(e) => setOrderChoice(e.target.value)}>
                <option value="">Automático (según la fecha de entrega)</option>
                {initialOrderId && !clientOrders.some((order: any) => String(order.id) === initialOrderId) && <option value={initialOrderId}>Pedido eliminado</option>}
                {clientOrders.map((order: any) => <option key={order.id} value={String(order.id)}>{order.label || order.month} · pedido {(Number(order.orderedBags || 0) / PACK_SIZE).toLocaleString('es-AR')} packs · faltan {(Number(order.pendingBags || 0) / PACK_SIZE).toLocaleString('es-AR')}</option>)}
              </select>
              <span className="mt-1 block text-xs font-normal text-gray-500">Por ejemplo, para pasar un remito del pedido mensual a un pedido especial. Las bolsas dejan de contar en el pedido anterior.</span>
            </label>
          )}
          <DetailLinesEditor lines={lines} onUpdate={updateLine} onAdd={addLine} onRemove={removeLine} priceOptions={priceOptions} monthOptions={orderChoice ? undefined : monthOptions} />
        </div>
        <div className="flex items-center justify-end border-t border-gray-100 px-6 py-4 gap-3">
          <button onClick={onClose} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">Cancelar</button>
          <button onClick={save} disabled={isSaving} className="rounded-lg bg-primary-600 px-4 py-2 text-sm text-white disabled:opacity-50">
            {isSaving ? 'Guardando...' : 'Guardar productos'}
          </button>
        </div>
      </div>
    </div>
  );
};

const RowMenu: React.FC<{ open: boolean; onToggle: () => void; onClose: () => void; items: Array<{ label: string; onClick: () => void; danger?: boolean }> }> = ({ open, onToggle, onClose, items }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={onToggle} title="Más acciones" className="rounded-lg border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-50"><MoreHorizontal className="h-4 w-4" /></button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-44 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {items.map((item) => (
            <button key={item.label} type="button" onClick={() => { onClose(); item.onClick(); }} className={`block w-full px-3 py-2 text-left text-sm ${item.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'}`}>{item.label}</button>
          ))}
        </div>
      )}
    </div>
  );
};

const Recibos: React.FC = () => {
  const [searchParams] = useSearchParams();
  const receiptType = 'entrega' as const;
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerCompany, setCustomerCompany] = useState('');
  const [customerCuit, setCustomerCuit] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [clients, setClients] = useState<User[]>([]);
  const [clientPrices, setClientPrices] = useState<any[]>([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [monthlyOrders, setMonthlyOrders] = useState<any[]>([]);
  const [selectedMonthlyOrderId, setSelectedMonthlyOrderId] = useState('');
  const currentOrderMonth = new Date().toLocaleDateString('sv-SE').slice(0, 7);
  const hasEarlierPendingOrders = monthlyOrders.some(order => order.month < currentOrderMonth && Number(order.pendingBags||0)>0);
  const [items, setItems] = useState<DetailLine[]>([emptyDetailLine('bolsas')]);
  const [note, setNote] = useState('');
  const todayIso = new Date().toLocaleDateString('sv-SE');
  const [deliveryDate, setDeliveryDate] = useState(todayIso);
  // A date typed by hand is kept; otherwise each new remito starts on today's date, even if the page stayed open for days.
  const [deliveryDateTouched, setDeliveryDateTouched] = useState(false);
  const [receiptNumber, setReceiptNumber] = useState('');
  const [registerReceipt, setRegisterReceipt] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [formError, setFormError] = useState('');
  const [stockWarnings, setStockWarnings] = useState<StockWarning[]>([]);

  const [receipts, setReceipts] = useState<ReceiptRow[]>([]);
  const clientPriceLists = useClientPriceLists(receipts.map((r) => r.clientId));
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState('');
  const [convertingId, setConvertingId] = useState<number | null>(null);
  const [filterClientId, setFilterClientId] = useState(searchParams.get('clientId') || '');
  const [editingClientRow, setEditingClientRow] = useState<ReceiptRow | null>(null);
  const [editClientSelection, setEditClientSelection] = useState('');
  const [isSavingClient, setIsSavingClient] = useState(false);
  const [editClientError, setEditClientError] = useState('');
  const [editingItemsRow, setEditingItemsRow] = useState<ReceiptRow | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(!!searchParams.get('clientId'));
  const [showClientFields, setShowClientFields] = useState(false);
  const [showRecentPrices, setShowRecentPrices] = useState(false);
  const [lastCreated, setLastCreated] = useState('');
  const [search, setSearch] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [menuId, setMenuId] = useState<number | null>(null);
  useBodyScrollLock(showCreateForm);
  useEscapeToClose(showCreateForm && !editingClientRow && !editingItemsRow, () => setShowCreateForm(false));
  useBodyScrollLock(!!editingClientRow);
  useEscapeToClose(!!editingClientRow, () => closeEditClient());

  const loadNextNumber = async () => {
    try {
      const data = await apiService.getNextReceiptNumber();
      setReceiptNumber(data.receiptNumber || '');
    } catch {
      // Silencio: puede no estar disponible temporalmente.
    }
  };

  const loadClients = async () => {
    try {
      const data = await apiService.getUsers({ role: 'cliente', page: 1, limit: 500 });
      setClients(data.users || []);
      const preselect = searchParams.get('clientId');
      if (preselect && data.users?.some((c) => String(c.id) === preselect)) {
        handleClientSelect(preselect);
      }
    } catch {
      setClients([]);
    }
  };

  const loadReceipts = async (clientId?: string) => {
    try {
      setIsLoadingList(true);
      const id = clientId !== undefined ? clientId : filterClientId;
      const data = await apiService.getReceipts({ page: 1, limit: 100, clientId: id ? Number(id) : undefined, includePayments: true });
      setReceipts(data.receipts || []);
    } catch {
      setReceipts([]);
    } finally {
      setIsLoadingList(false);
    }
  };

  useEffect(() => {
    loadClients();
    loadReceipts(searchParams.get('clientId') || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const targetId=Number(searchParams.get('receiptId')||0);
    if(!targetId||isLoadingList||!receipts.some(receipt=>receipt.id===targetId)) return;
    setExpandedId(targetId);
    window.setTimeout(()=>document.getElementById(`receipt-row-${targetId}`)?.scrollIntoView({behavior:'smooth',block:'center'}),0);
  },[isLoadingList,receipts,searchParams]);

  useEffect(() => {
    if (!registerReceipt) return;
    loadNextNumber();
  }, [registerReceipt]);

  const priceForLine = (line: DetailLine, prices: any[]) => prices.find(price =>
    Number(price.widthCm) === Number(line.anchoCm) && Number(price.lengthCm) === Number(line.largoCm) &&
    Number(price.microns || 0) === Number(line.micronaje || 0) && String(price.color || '').toLowerCase() === String(line.color || '').toLowerCase()
  );
  const applyListedPrice = (line: DetailLine, prices: any[]): DetailLine => {
    if (line.mode !== 'bolsas') return line;
    const price = priceForLine(line, prices);
    if (!price) return { ...line, priceListId: undefined };
    return { ...line, costUnit:Number(price.costUnit||0), saleNetUnit:Number(price.saleNetUnit||0), vatRate:Number(price.vatRate??21), purchaseHasVat:Boolean(price.purchaseHasVat),
      marginPercent:Number(price.marginPercent ?? (price.costUnit ? ((price.saleNetUnit/price.costUnit)-1)*100 : 0)), priceListId:Number(price.id) };
  };

  const handleClientSelect = async (value: string) => {
    setSelectedClientId(value);
    setShowClientFields(false);
    setSelectedMonthlyOrderId('');
    if (!value) { setClientPrices([]); setMonthlyOrders([]); return; }
    const client = clients.find((x) => String(x.id) === value);
    if (client) {
      setCustomerName(client.name || ''); setCustomerEmail(client.email || ''); setCustomerCompany(client.company || '');
      setCustomerCuit(client.cuit || client.taxId || ''); setCustomerPhone(client.phone || '');
    }
    try {
      const data = await apiService.getClientPrices(Number(value));
      const prices = data.prices || [];
      setClientPrices(prices);
      setItems(current => current.map(line => applyListedPrice(line, prices)));
    } catch { setClientPrices([]); }
    try {
      const monthly = await apiService.getMonthlyOrders({ clientId: Number(value) });
      setMonthlyOrders((monthly.orders || []).filter((order: any) => Number(order.pendingBags || 0) > 0));
    } catch { setMonthlyOrders([]); }
  };

  const handleFilterClientChange = (value: string) => {
    setFilterClientId(value);
    loadReceipts(value);
  };

  const exportReceipts = async () => {
    try {
      setIsExporting(true);
      setDownloadError('');
      const response = await apiService.getReceipts({
        page: 1,
        limit: 10000,
        clientId: filterClientId ? Number(filterClientId) : undefined,
        includePayments: true,
      });
      const exportRows = (response.receipts as ReceiptRow[]).flatMap<Record<string, string | number>>((receipt) => {
        const common = {
          'N° remito': receipt.receiptNumber,
          'Fecha': fmtDate(receipt.createdAt),
          'Cliente': receipt.customerName,
          'Estado': receipt.receiptType === 'entrega'
            ? (receipt.clientId ? 'Remito' : 'Sin cliente asociado')
            : 'Pendiente convertir',
        };
        if (!receipt.items?.length) {
          return [{
            ...common,
            'Medida / detalle': receipt.concept,
            'Color': '',
            'Packs': '',
            'Bolsas sueltas': '',
            'Bolsas totales': '',
            'Descuenta stock propio': '',
            'Nota del producto': '',
          }];
        }
        return receipt.items.map((item) => ({
          ...common,
          'Medida / detalle': item.productName,
          'Color': item.productColor,
          'Packs': item.packs,
          'Bolsas sueltas': item.looseBags,
          'Bolsas totales': item.quantity,
          'Descuenta stock propio': item.deduct ? 'Sí' : 'No',
          'Nota del producto': item.itemNote || '',
        }));
      });
      if (!exportRows.length) {
        setDownloadError('No hay remitos para exportar con el filtro seleccionado.');
        return;
      }
      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const columns = Object.keys(exportRows[0]);
      worksheet['!cols'] = columns.map((key) => ({
        wch: Math.min(60, Math.max(key.length, ...exportRows.map((row) => String((row as any)[key] ?? '').length)) + 2),
      }));
      worksheet['!autofilter'] = { ref: worksheet['!ref'] || `A1:${XLSX.utils.encode_col(columns.length - 1)}1` };
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Remitos');
      const selectedClient = clients.find((client) => String(client.id) === filterClientId);
      const clientSuffix = selectedClient
        ? `-${String(selectedClient.company || selectedClient.name || 'cliente').replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ]+/g, '-').replace(/^-|-$/g, '')}`
        : '';
      XLSX.writeFile(workbook, `remitos${clientSuffix}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error: any) {
      setDownloadError(error?.response?.data?.detail || 'No se pudieron exportar los remitos.');
    } finally {
      setIsExporting(false);
    }
  };

  const updateItem = (index: number, patch: Partial<DetailLine>) => {
    setItems(current => current.map((line, i) => {
      if (i !== index) return line;
      const updated = { ...line, ...patch };
      const productChanged = ['anchoCm','largoCm','color','micronaje','mode'].some(key => Object.prototype.hasOwnProperty.call(patch, key));
      if (productChanged && clientPrices.length) return applyListedPrice(updated, clientPrices);
      const commercialChanged = ['costUnit','saleNetUnit','vatRate','marginPercent','purchaseHasVat'].some(key => Object.prototype.hasOwnProperty.call(patch, key));
      return commercialChanged ? { ...updated, priceListId: undefined } : updated;
    }));
  };

  const addItem = () => setItems((current) => [...current, emptyDetailLine('bolsas')]);

  // Pending articles of the order chosen for this delivery (none for the automatic/current option).
  const selectedOrder = selectedMonthlyOrderId && selectedMonthlyOrderId !== 'current' ? monthlyOrders.find((order: any) => String(order.id) === selectedMonthlyOrderId) : null;
  const pendingOrderItems: any[] = selectedOrder ? (selectedOrder.items || []).filter((item: any) => Number(item.pendingBags || 0) > 0) : [];
  const isPendingArticle = (price: any) => pendingOrderItems.some((item: any) => Number(item.ancho) === Number(price.widthCm) && Number(item.largo) === Number(price.lengthCm) && item.color === price.color
    && (!Number(item.microns || 0) || Number(item.microns) === Number(price.microns || 0)));
  // Lines of the remito that go over what is still missing of the chosen order (the server rejects them).
  const overPendingMessages: string[] = !selectedOrder || receiptType !== 'entrega' ? [] : (() => {
    const loaded = new Map<string, { bags: number; label: string }>();
    items.forEach((line) => {
      if (line.mode !== 'bolsas' || !line.cantidad) return;
      const key = `${Number(line.anchoCm)}x${Number(line.largoCm)}-${line.color}`;
      const current = loaded.get(key) || { bags: 0, label: `${line.anchoCm} × ${line.largoCm} ${COLOR_LABELS[line.color] || line.color}` };
      loaded.set(key, { ...current, bags: current.bags + lineBags(line) });
    });
    const messages: string[] = [];
    loaded.forEach(({ bags, label }, key) => {
      const item = (selectedOrder.items || []).find((i: any) => `${Number(i.ancho)}x${Number(i.largo)}-${i.color}` === key);
      const pending = item ? Number(item.pendingBags || 0) : 0;
      if (!item) messages.push(`${label}: no forma parte del pedido ${selectedOrder.label || selectedOrder.month}`);
      else if (bags > pending) messages.push(`${label}: cargaste ${(bags / PACK_SIZE).toLocaleString('es-AR')} packs y faltan ${(pending / PACK_SIZE).toLocaleString('es-AR')}`);
    });
    return messages;
  })();
  const orderPriceOptions = pendingOrderItems.length && clientPrices.some(isPendingArticle) ? clientPrices.filter(isPendingArticle) : clientPrices;

  // Choosing an order loads what is still missing; the quantities can then be lowered for a partial delivery.
  const selectMonthlyOrder = (value: string) => {
    setSelectedMonthlyOrderId(value);
    const order = value && value !== 'current' ? monthlyOrders.find((o: any) => String(o.id) === value) : null;
    const pending = order ? (order.items || []).filter((item: any) => Number(item.pendingBags || 0) > 0) : [];
    if (!pending.length) return;
    if (items.some((line) => line.mode === 'libre' ? line.texto.trim() : line.cantidad > 0) && !window.confirm('¿Reemplazar los productos cargados por lo que falta entregar de este pedido?')) return;
    setItems(pending.map((item: any) => {
      const bags = Number(item.pendingBags);
      const line: DetailLine = { ...emptyDetailLine('bolsas'), anchoCm: Number(item.ancho), largoCm: Number(item.largo), color: item.color as ColorKey,
        micronaje: Number(item.microns || 0), unidad: bags % PACK_SIZE ? 'bolsas' : 'packs', cantidad: bags % PACK_SIZE ? bags : bags / PACK_SIZE };
      // An order without microns takes them from the client's price list, so the price is found too.
      const listed = clientPrices.find((price: any) => Number(price.widthCm) === line.anchoCm && Number(price.lengthCm) === line.largoCm && price.color === line.color
        && (!line.micronaje || Number(price.microns || 0) === line.micronaje));
      return listed ? applyListedPrice({ ...line, micronaje: Number(listed.microns || 0) }, clientPrices) : line;
    }));
  };

  const removeItem = (index: number) => {
    setItems((current) => (current.length > 1 ? current.filter((_, i) => i !== index) : current));
  };

  const resetForm = () => {
    setSelectedClientId('');
    setCustomerName('');
    setCustomerEmail('');
    setCustomerCompany('');
    setCustomerCuit('');
    setCustomerPhone('');
    setItems([emptyDetailLine('bolsas')]);
    setClientPrices([]);
    setMonthlyOrders([]);
    setSelectedMonthlyOrderId('');
    setNote('');
    setDeliveryDate(new Date().toLocaleDateString('sv-SE'));
    setDeliveryDateTouched(false);
  };

  const openCreate = () => {
    setFormError('');
    if (!deliveryDateTouched) setDeliveryDate(new Date().toLocaleDateString('sv-SE'));
    setShowCreateForm(true);
  };

  const generateReceipt = async () => {
    setFormError('');

    if (receiptType === 'entrega' && selectedClientId && hasEarlierPendingOrders && !selectedMonthlyOrderId) {
      setFormError('Hay pedidos de meses anteriores pendientes. Elegí a qué mes corresponde esta entrega.');
      return;
    }
    if (overPendingMessages.length) {
      setFormError(`La entrega supera lo que falta del pedido elegido. ${overPendingMessages.join(' · ')}. Corregí las cantidades o elegí "Entrega del mes actual".`);
      return;
    }
    const cleanItems = items.map(describeDetailLine).filter(Boolean);
    if (!customerName.trim()) {
      setFormError('Completa el nombre del cliente.');
      return;
    }
    if (cleanItems.length === 0) {
      setFormError('Agrega al menos una linea de detalle.');
      return;
    }

    try {
      setIsGenerating(true);
      setStockWarnings([]);
      const cfg = getPricingConfig();
      const { blob, filename, stockWarnings: warnings } = await apiService.generateReceiptPdf({
        receiptNumber: receiptNumber.trim(),
        registerReceipt,
        receiptType,
        clientId: selectedClientId ? Number(selectedClientId) : undefined,
        monthlyOrderId: receiptType === 'entrega' && selectedMonthlyOrderId && selectedMonthlyOrderId !== 'current' ? Number(selectedMonthlyOrderId) : undefined,
        customerName,
        customerEmail,
        customerCompany,
        customerCuit,
        customerPhone,
        items: cleanItems,
        stockItems: receiptType === 'entrega' ? items.map(detailLineToRaw).filter((x): x is RawDetailLine => x !== null) : [],
        amountARS: 0,
        paymentMethod: '',
        deliveryDate,
        note,
        template: cfg.pdfTemplate
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setStockWarnings(warnings);
      setLastCreated(registerReceipt ? `Remito ${receiptNumber.trim() || filename} registrado y descargado.` : `PDF ${filename} generado (sin registrar).`);
      setShowCreateForm(false);
      resetForm();
      if (registerReceipt) {
        await loadNextNumber();
        await loadReceipts();
      }
    } catch (error: any) {
      setFormError(error?.response?.data?.detail || error?.message || 'No se pudo generar el remito.');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadReceipt = async (id: number) => {
    try {
      setDownloadingId(id);
      setDownloadError('');
      const { blob, filename } = await apiService.downloadReceiptPdf(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      let detail = error?.response?.data?.detail;
      if (error?.response?.data instanceof Blob) {
        try {
          const body = JSON.parse(await error.response.data.text());
          detail = body?.detail;
        } catch {
          // La respuesta no era JSON; se usa el mensaje segun el estado HTTP.
        }
      }
      if (error?.response?.status === 404) {
        setDownloadError(detail || 'No se encontró este remito. Actualizá la lista y verificá que el backend publicado esté actualizado.');
      } else {
        setDownloadError(detail || 'No se pudo descargar el PDF del remito.');
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const convertToRemito = async (row: ReceiptRow) => {
    if (!row.clientId) {
      setDownloadError('Primero asigná un cliente al documento y luego convertílo a remito.');
      return;
    }
    if (!window.confirm(`Convertir ${row.receiptNumber} a remito? Se conservarán su fecha y numeración.`)) return;
    try {
      setConvertingId(row.id);
      setDownloadError('');
      const response = await apiService.convertReceiptToDelivery(row.id);
      setReceipts((current) => current.map((item) => item.id === row.id ? { ...item, ...response.receipt } : item));
    } catch (error: any) {
      setDownloadError(error?.response?.data?.detail || 'No se pudo convertir el documento a remito.');
    } finally {
      setConvertingId(null);
    }
  };
  const deleteReceipt = async (row: ReceiptRow) => {
    if (!window.confirm(`¿Eliminar el remito ${row.receiptNumber}? Esta acción no se puede deshacer y revierte el stock que haya cargado.`)) return;
    try {
      setDeletingId(row.id);
      setDownloadError('');
      await apiService.deleteReceipt(row.id);
      setReceipts((current) => current.filter((item) => item.id !== row.id));
    } catch (error: any) {
      setDownloadError(error?.response?.data?.detail || 'No se pudo eliminar el remito.');
    } finally {
      setDeletingId(null);
    }
  };

  const openEditClient = (row: ReceiptRow) => {
    setEditingClientRow(row);
    setEditClientSelection(row.clientId ? String(row.clientId) : '');
    setEditClientError('');
  };

  const closeEditClient = () => {
    setEditingClientRow(null);
    setEditClientError('');
    setIsSavingClient(false);
  };

  const saveEditClient = async () => {
    if (!editingClientRow) return;
    try {
      setIsSavingClient(true);
      setEditClientError('');
      const clientId = editClientSelection ? Number(editClientSelection) : null;
      const response = await apiService.updateReceiptClient(editingClientRow.id, clientId);
      setReceipts((prev) => prev.map((r) => (r.id === editingClientRow.id ? { ...r, ...response.receipt } : r)));
      closeEditClient();
    } catch (error: any) {
      setEditClientError(error?.response?.data?.detail || 'No se pudo actualizar el cliente del remito.');
    } finally {
      setIsSavingClient(false);
    }
  };

  const [editingDateRow, setEditingDateRow] = useState<ReceiptRow | null>(null);
  const [editDateValue, setEditDateValue] = useState('');
  const [editDateError, setEditDateError] = useState('');
  const [isSavingDate, setIsSavingDate] = useState(false);
  const openEditDate = (row: ReceiptRow) => { setEditingDateRow(row); setEditDateValue(String(row.deliveryDate || '').slice(0, 10)); setEditDateError(''); };
  const saveEditDate = async () => {
    if (!editingDateRow || !editDateValue) return;
    try {
      setIsSavingDate(true);
      setEditDateError('');
      const response = await apiService.updateReceiptDeliveryDate(editingDateRow.id, editDateValue);
      setReceipts((prev) => prev.map((r) => (r.id === editingDateRow.id ? { ...r, deliveryDate: response.deliveryDate } : r)));
      setEditingDateRow(null);
    } catch (error: any) {
      setEditDateError(error?.response?.data?.detail || 'No se pudo cambiar la fecha de entrega.');
    } finally {
      setIsSavingDate(false);
    }
  };

  const openEditItems = (row: ReceiptRow) => setEditingItemsRow(row);
  const closeEditItems = () => setEditingItemsRow(null);

  const fmtDate = (iso: string) => {
    try {
      return new Date(iso.replace(' ', 'T') + 'Z').toLocaleString('es-AR');
    } catch {
      return iso;
    }
  };

  const fmtDay = (iso: string) => {
    const date = new Date(String(iso || '').replace(' ', 'T') + (String(iso || '').length > 10 ? 'Z' : 'T00:00:00'));
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString('es-AR');
  };
  const localMonth = (iso: string) => {
    const date = new Date(String(iso || '').replace(' ', 'T') + 'Z');
    return Number.isNaN(date.getTime()) ? String(iso || '').slice(0, 7) : date.toLocaleDateString('sv-SE').slice(0, 7);
  };
  const bagsLabel = (bags: number) => (bags % PACK_SIZE === 0 ? `${(bags / PACK_SIZE).toLocaleString('es-AR')} packs` : `${bags.toLocaleString('es-AR')} bolsas`);
  const receiptSummary = (r: ReceiptRow) => {
    if (!r.items?.length) return r.concept.length > 70 ? `${r.concept.slice(0, 70)}…` : r.concept;
    const bags = r.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    return `${r.items.length} línea${r.items.length === 1 ? '' : 's'} · ${bagsLabel(bags)}`;
  };
  const term = search.trim().toLowerCase();
  const visibleReceipts = receipts.filter((r) =>
    (!filterMonth || (r.deliveryDate ? r.deliveryDate.slice(0, 7) : localMonth(r.createdAt)) === filterMonth) &&
    (!term || [r.receiptNumber, r.customerName, r.concept].some((value) => String(value || '').toLowerCase().includes(term))));
  const formLines = items.filter((line) => describeDetailLine(line));
  const formBags = formLines.filter((line) => line.mode === 'bolsas').reduce((sum, line) => sum + lineBags(line), 0);
  const formTotal = formLines.filter((line) => line.mode === 'bolsas').reduce((sum, line) => sum + line.saleNetUnit * (1 + line.vatRate / 100) * lineBags(line), 0);
  const formSummary = formLines.length
    ? `${formLines.length} línea${formLines.length === 1 ? '' : 's'}${formBags ? ` · ${bagsLabel(formBags)}` : ''}${formTotal > 0 ? ` · ${money(formTotal)} con IVA` : ''}`
    : 'Todavía no hay productos cargados.';

  const recentClientPrices = useMemo(() => {
    if (!selectedClientId) return [];
    const seen = new Set<string>();
    const result: Array<{ key:string; measure:string; color:string; microns:number; net:number; gross:number; vat:number; date:string }> = [];
    receipts.filter(receipt => String(receipt.clientId || '') === selectedClientId).forEach(receipt => {
      (receipt.rawItems || []).forEach(raw => {
        if (raw.mode !== 'bolsas' || !raw.saleNetUnit || raw.saleNetUnit <= 0) return;
        const key = `${raw.ancho}x${raw.largo}|${raw.color}|${raw.microns || 0}`;
        if (seen.has(key)) return;
        seen.add(key);
        const vat = Number(raw.vatRate ?? 21), net = Number(raw.saleNetUnit);
        result.push({ key, measure:`${raw.ancho} x ${raw.largo} cm`, color:COLOR_LABELS[raw.color as ColorKey] || raw.color, microns:Number(raw.microns || 0), net, gross:net*(1+vat/100), vat, date:receipt.createdAt });
      });
    });
    return result;
  }, [receipts, selectedClientId]);
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Remitos</h1>
          <p className="text-gray-600">Remitos de entrega a clientes. Al registrarlos, quedan en su panel y actualizan el stock.</p>
        </div>
        <button onClick={openCreate} className="inline-flex items-center gap-2 self-start rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700">
          <Plus className="h-4 w-4" /> Crear remito
        </button>
      </div>

      {lastCreated && (
        <div className={`rounded-lg border p-3 text-sm ${stockWarnings.length ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-green-200 bg-green-50 text-green-800'}`}>
          <div className="flex items-start justify-between gap-3">
            <p className="font-medium">{lastCreated}</p>
            <button onClick={() => { setLastCreated(''); setStockWarnings([]); }} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
          </div>
          {stockWarnings.length > 0 && (
            <>
              <p className="mt-1">No hay stock propio suficiente cargado para:</p>
              <ul className="mt-1 list-disc pl-5">
                {stockWarnings.map((w, i) => <li key={i}>{w.productName}: faltan {w.shortfall.toLocaleString('es-AR')} bolsas (disponibles {w.available.toLocaleString('es-AR')}, se entregaron {w.requested.toLocaleString('es-AR')}).</li>)}
              </ul>
              <p className="mt-1">Revisá si falta cargar una entrada de proveedor en Stock propio.</p>
            </>
          )}
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-gray-100 p-4 lg:flex-row lg:items-center lg:justify-between">
          <h2 className="font-semibold text-gray-900">Remitos emitidos <span className="text-sm font-normal text-gray-500">({visibleReceipts.length}{visibleReceipts.length !== receipts.length ? ` de ${receipts.length}` : ''})</span></h2>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar N°, cliente o medida" className="w-56 rounded-lg border border-gray-300 py-2 pl-8 pr-3 text-sm" />
            </div>
            <select className="max-w-[14rem] rounded-lg border border-gray-300 px-3 py-2 text-sm" value={filterClientId} onChange={(e) => handleFilterClientChange(e.target.value)}>
              <option value="">Todos los clientes</option>
              {clients.map((client) => <option key={client.id} value={client.id}>{client.company || client.name}</option>)}
            </select>
            <input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} title="Filtrar por mes" className="rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            <WeightToggle className="py-2" />
            <button type="button" onClick={exportReceipts} disabled={isExporting || isLoadingList} className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {isExporting ? 'Exportando...' : 'Exportar Excel'}
            </button>
          </div>
        </div>

        {downloadError && <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{downloadError}</div>}

        {isLoadingList ? (
          <div className="p-6 text-sm text-gray-500">Cargando...</div>
        ) : visibleReceipts.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">{receipts.length ? 'No hay remitos para estos filtros.' : filterClientId ? 'Este cliente todavía no tiene remitos.' : 'Todavía no hay remitos generados.'}</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {visibleReceipts.map((r) => {
              const open = expandedId === r.id;
              return (
                <li id={`receipt-row-${r.id}`} key={r.id} className={open ? 'bg-amber-50 ring-2 ring-inset ring-amber-300' : ''}>
                  <div className="flex items-center gap-3 px-4 py-3">
                    <button type="button" onClick={() => setExpandedId(open ? null : r.id)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
                      {open ? <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" /> : <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />}
                      <div className="grid min-w-0 flex-1 grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-[8rem_6rem_minmax(0,1fr)_minmax(0,1.2fr)] sm:items-center">
                        <span className="font-semibold text-gray-900">{r.receiptNumber}</span>
                        <span className="text-xs text-gray-500 sm:text-sm" title={`Entregado el ${r.deliveryDate ? fmtDay(r.deliveryDate) : fmtDay(r.createdAt)} · cargado el ${fmtDate(r.createdAt)}`}>{r.deliveryDate ? fmtDay(r.deliveryDate) : fmtDay(r.createdAt)}</span>
                        <span className="truncate text-sm font-medium text-gray-800">{r.customerName}</span>
                        <span className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                          <span className="truncate">{receiptSummary(r)}</span>
                          {r.items?.map((item, index) => (
                            <span key={index} title={`${item.productName} ${item.productColor}${item.productMicrons ? ` ${item.productMicrons}μ` : ' sin micronaje'} · ${bagsLabel(Number(item.quantity || 0))}`} className="whitespace-nowrap rounded border border-gray-200 bg-white px-1.5 py-0.5 text-gray-700">
                              {item.productName.replace(/\s*cm\b/i, '').replace(/\s*×\s*/g, '×')} {item.productColor} <strong className={item.productMicrons ? 'text-gray-900' : 'text-red-600'}>{item.productMicrons ? `${item.productMicrons}μ` : 's/μ'}</strong> · {bagsLabel(Number(item.quantity || 0)).replace(' packs', 'p')}
                            </span>
                          ))}
                          {r.receiptType !== 'entrega' && <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-800">Pendiente convertir</span>}
                          {r.receiptType === 'entrega' && !r.clientId && <span className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-700">Sin cliente</span>}
                          {r.receiptType === 'entrega' && !r.deductWarehouseStock && <span title="Alguna línea no descuenta de Stock propio" className="rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-600">Otro proveedor</span>}
                        </span>
                      </div>
                    </button>
                    <div className="flex shrink-0 items-center gap-1">
                      {r.receiptType !== 'entrega' && (
                        <button onClick={() => convertToRemito(r)} disabled={convertingId === r.id} className="rounded-lg bg-amber-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-60">
                          {convertingId === r.id ? 'Convirtiendo...' : 'Convertir'}
                        </button>
                      )}
                      <button onClick={() => downloadReceipt(r.id)} disabled={downloadingId === r.id} title="Descargar PDF" className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60">
                        <FileDown className="h-3.5 w-3.5" />{downloadingId === r.id ? '...' : 'PDF'}
                      </button>
                      <RowMenu
                        open={menuId === r.id}
                        onToggle={() => setMenuId(menuId === r.id ? null : r.id)}
                        onClose={() => setMenuId(null)}
                        items={[
                          ...(r.receiptType === 'entrega' ? [{ label: 'Editar productos', onClick: () => openEditItems(r) }] : []),
                          ...(r.receiptType === 'entrega' ? [{ label: 'Cambiar fecha de entrega', onClick: () => openEditDate(r) }] : []),
                          { label: 'Cambiar cliente', onClick: () => openEditClient(r) },
                          { label: deletingId === r.id ? 'Eliminando...' : 'Eliminar remito', onClick: () => deleteReceipt(r), danger: true },
                        ]}
                      />
                    </div>
                  </div>
                  {open && (
                    <div className="px-4 pb-4 sm:pl-11">
                      {r.items?.length
                        ? <ReceiptItemsTable items={r.items} prices={r.clientId ? clientPriceLists[r.clientId] : undefined} className="overflow-x-auto rounded-lg border border-gray-200 bg-white" />
                        : <p className="text-sm text-gray-600">{r.concept}</p>}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showCreateForm && (
        <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center sm:p-4" onClick={() => setShowCreateForm(false)}>
          <div className="flex max-h-[96vh] w-full max-w-4xl flex-col rounded-t-xl bg-gray-50 shadow-xl sm:max-h-[92vh] sm:rounded-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 bg-white px-5 py-3 sm:rounded-t-xl">
              <h2 className="text-lg font-semibold text-gray-900">Nuevo remito</h2>
              <button onClick={() => setShowCreateForm(false)} className="rounded-md p-1 text-gray-500 hover:bg-gray-100"><X className="h-5 w-5" /></button>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-5">
              <section className="rounded-xl border border-gray-200 bg-white p-4">
                <h3 className="mb-3 text-sm font-semibold text-gray-900"><span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-xs text-white">1</span>Cliente</h3>
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={selectedClientId} onChange={(e) => handleClientSelect(e.target.value)}>
                  <option value="">Cargar datos manualmente</option>
                  {clients.map((client) => <option key={client.id} value={client.id}>{(client.company || client.name)} - {client.email}</option>)}
                </select>
                {selectedClientId && !showClientFields ? (
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600">
                    <span><strong className="text-gray-900">{customerCompany || customerName}</strong>{customerCuit ? ` · CUIT ${customerCuit}` : ''}{customerPhone ? ` · ${customerPhone}` : ''}</span>
                    <button type="button" onClick={() => setShowClientFields(true)} className="text-xs font-medium text-primary-600 hover:underline">Editar datos</button>
                  </div>
                ) : (
                  <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    <label className="text-xs text-gray-500">Nombre *<input className="mt-0.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={customerName} onChange={(e) => setCustomerName(e.target.value)} /></label>
                    <label className="text-xs text-gray-500">Empresa<input className="mt-0.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={customerCompany} onChange={(e) => setCustomerCompany(e.target.value)} /></label>
                    <label className="text-xs text-gray-500">CUIT / DNI<input className="mt-0.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={customerCuit} onChange={(e) => setCustomerCuit(e.target.value)} /></label>
                    <label className="text-xs text-gray-500">Teléfono<input className="mt-0.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} /></label>
                    <label className="text-xs text-gray-500">Email<input className="mt-0.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} /></label>
                  </div>
                )}
                {receiptType === 'entrega' && selectedClientId && (
                  <div className="mt-3">
                    <label className="text-xs font-medium text-gray-700">¿A qué pedido corresponde esta entrega?
                      <select className="mt-0.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={selectedMonthlyOrderId} onChange={e => selectMonthlyOrder(e.target.value)}>
                        <option value="">{hasEarlierPendingOrders ? 'Seleccionar el mes de la entrega...' : 'Entrega del mes actual'}</option>
                        <option value="current">Entrega del mes actual ({currentOrderMonth})</option>
                        {monthlyOrders.map((order: any) => <option key={order.id} value={order.id}>{order.month > currentOrderMonth ? `Adelanto para ${order.label || order.month}` : (order.label || order.month)} · faltan {(Number(order.pendingBags || 0) / PACK_SIZE).toLocaleString('es-AR')} packs · urgente {(Number(order.urgentPendingBags || 0) / PACK_SIZE).toLocaleString('es-AR')} packs</option>)}
                      </select>
                    </label>
                    {hasEarlierPendingOrders && <p className="mt-1 text-xs font-medium text-amber-800">Hay pedidos anteriores pendientes. Elegí el pedido que estás entregando; si corresponde a este mes, seleccioná la entrega del mes actual.</p>}
                    {selectedMonthlyOrderId && selectedMonthlyOrderId !== 'current' && <div className="mt-1 space-y-0.5 text-xs text-blue-800">{monthlyOrders.find((order: any) => String(order.id) === selectedMonthlyOrderId)?.items.filter((item: any) => item.pendingBags > 0).map((item: any, index: number) => <p key={index}>{item.productName}: faltan {(item.pendingBags / PACK_SIZE).toLocaleString('es-AR')} packs{item.urgentPendingBags > 0 ? ` · urgente ${(item.urgentPendingBags / PACK_SIZE).toLocaleString('es-AR')}` : ''}</p>)}</div>}
                  </div>
                )}
              </section>

              <section className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-gray-900"><span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-xs text-white">2</span>Productos</h3>
                  {selectedClientId && <button type="button" onClick={() => setShowRecentPrices((v) => !v)} className="text-xs font-medium text-primary-600 hover:underline">{showRecentPrices ? 'Ocultar últimos precios' : 'Ver últimos precios del cliente'}</button>}
                </div>
                {selectedClientId && showRecentPrices && (
                  <div className="mb-3 rounded-lg border bg-gray-50 p-3">
                    <p className="text-xs text-gray-500">Una referencia por medida, color y micronaje. No se imprimen.</p>
                    {!recentClientPrices.length ? <p className="mt-2 text-sm text-gray-500">Todavía no hay precios guardados para este cliente.</p> : <div className="mt-2 overflow-x-auto"><table className="min-w-full text-sm"><thead><tr className="border-b text-left text-xs text-gray-500"><th className="py-1.5 pr-3">Medida</th><th className="py-1.5 pr-3">Color</th><th className="py-1.5 pr-3">Micrones</th><th className="py-1.5 pr-3 text-right">Sin IVA</th><th className="py-1.5 pr-3 text-right">Final con IVA</th><th className="py-1.5">Fecha</th></tr></thead><tbody>{recentClientPrices.map(price => <tr key={price.key} className="border-b last:border-0"><td className="py-1.5 pr-3 font-medium">{price.measure}</td><td className="py-1.5 pr-3">{price.color}</td><td className="py-1.5 pr-3">{price.microns || '-'}</td><td className="py-1.5 pr-3 text-right">{money(price.net, 5)}</td><td className="py-1.5 pr-3 text-right font-semibold">{money(price.gross, 5)}</td><td className="whitespace-nowrap py-1.5 text-xs text-gray-500">{fmtDay(price.date)}</td></tr>)}</tbody></table></div>}
                  </div>
                )}
                {pendingOrderItems.length > 0 && <p className="mb-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">Se cargó lo que falta del pedido {selectedOrder?.label || selectedOrder?.month}. Bajá las cantidades si esta entrega es parcial. La lista de precios muestra solo esas medidas.</p>}
                <DetailLinesEditor lines={items} onUpdate={updateItem} onAdd={addItem} onRemove={removeItem} priceOptions={selectedClientId ? orderPriceOptions : undefined} />
                {overPendingMessages.length > 0 && <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"><p className="font-semibold">Supera lo que falta del pedido elegido:</p>{overPendingMessages.map((message) => <p key={message}>{message}</p>)}</div>}
              </section>

              <section className="rounded-xl border border-gray-200 bg-white p-4">
                <h3 className="mb-3 text-sm font-semibold text-gray-900"><span className="mr-2 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-xs text-white">3</span>Datos del remito</h3>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[10rem_12rem_minmax(0,1fr)]">
                  <label className="text-xs text-gray-500">Fecha de entrega
                    <input type="date" className="mt-0.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={deliveryDate} max={todayIso} onChange={(e) => { setDeliveryDateTouched(true); setDeliveryDate(e.target.value || todayIso); }} />
                  </label>
                  <label className="text-xs text-gray-500">N° remito
                    <input className="mt-0.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={receiptNumber} onChange={(e) => setReceiptNumber(e.target.value)} placeholder={registerReceipt ? 'REC-2026-001' : 'BORRADOR-20260421-1200'} />
                  </label>
                  <label className="text-xs text-gray-500">Observaciones (opcional)
                    <input className="mt-0.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={note} onChange={(e) => setNote(e.target.value)} />
                  </label>
                </div>
                <label className="mt-3 inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={registerReceipt} onChange={(e) => setRegisterReceipt(e.target.checked)} />
                  Registrar el remito (queda guardado y actualiza el stock)
                </label>
                {!registerReceipt && <p className="mt-1 text-xs text-amber-700">Solo se genera el PDF: no queda guardado ni mueve stock.</p>}
              </section>
            </div>

            <div className="border-t border-gray-200 bg-white px-5 py-3 sm:rounded-b-xl">
              {formError && <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{formError}</div>}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-gray-600">{formSummary}</p>
                <div className="flex gap-2">
                  <button onClick={() => setShowCreateForm(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">Cerrar</button>
                  <button onClick={generateReceipt} disabled={isGenerating} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60">
                    {isGenerating ? 'Generando...' : registerReceipt ? 'Registrar y generar PDF' : 'Generar PDF'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingClientRow && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4" onClick={closeEditClient}>
          <div className="w-full max-w-md rounded-t-xl sm:rounded-xl bg-white shadow-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Editar cliente del remito</h2>
              <button onClick={closeEditClient} className="text-gray-500 hover:text-gray-700 text-xl leading-none">×</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="text-sm text-gray-600">
                Remito <span className="font-medium text-gray-900">{editingClientRow.receiptNumber}</span> · Cliente actual: <span className="font-medium text-gray-900">{editingClientRow.customerName}</span>
              </div>
              {editClientError && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{editClientError}</div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Cliente</label>
                <select
                  value={editClientSelection}
                  onChange={(e) => setEditClientSelection(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Sin vincular</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {(client.company || client.name)} - {client.email}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Al vincular un cliente, el nombre del remito se actualiza con el nombre registrado del cliente.</p>
              </div>
            </div>
            <div className="flex items-center justify-end border-t border-gray-100 px-6 py-4 gap-3">
              <button onClick={closeEditClient} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">Cancelar</button>
              <button onClick={saveEditClient} disabled={isSavingClient} className="rounded-lg bg-primary-600 px-4 py-2 text-sm text-white disabled:opacity-50">
                {isSavingClient ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingDateRow && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4" onClick={() => setEditingDateRow(null)}>
          <div className="w-full max-w-sm rounded-t-xl bg-white shadow-xl sm:rounded-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
              <h2 className="font-semibold text-gray-900">Fecha de entrega</h2>
              <button onClick={() => setEditingDateRow(null)} className="rounded-md p-1 text-gray-500 hover:bg-gray-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3 px-5 py-4">
              <p className="text-sm text-gray-600">Remito <strong>{editingDateRow.receiptNumber}</strong> · cargado el {fmtDate(editingDateRow.createdAt)}</p>
              {editDateError && <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-700">{editDateError}</div>}
              <input type="date" value={editDateValue} max={todayIso} onChange={(e) => setEditDateValue(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              <p className="text-xs text-gray-500">Cambia el mes en el que cuenta la entrega (pedidos mensuales y estadísticas) y la fecha impresa en el PDF.</p>
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
              <button onClick={() => setEditingDateRow(null)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">Cancelar</button>
              <button onClick={saveEditDate} disabled={isSavingDate || !editDateValue} className="rounded-lg bg-primary-600 px-4 py-2 text-sm text-white disabled:opacity-50">{isSavingDate ? 'Guardando...' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}

      {editingItemsRow && (
        <EditReceiptItemsModal
          row={editingItemsRow}
          onClose={closeEditItems}
          onSaved={(patch) => setReceipts((prev) => prev.map((r) => (r.id === editingItemsRow.id ? { ...r, ...patch } : r)))}
        />
      )}
    </div>
  );
};

export default Recibos;
